import {
  BiDataWidgetPlacement,
  getWidgetPlacement as getDefaultWidgetPlacement,
} from '@wix/wix-chatbot-common/bi';
import {
  Context,
  contextProvider,
  InitialParams,
  NavigationCompleted,
  NavigationParams,
  OrigParams,
  StartChatParams,
  WidgetSDK,
} from '@wix/wix-chatbot-common/browser';

import { MOBILE_APP_REFERRALS } from '../../constants';
import { logger } from '../util/logger';
import { fedopsLogger, renderModel } from '../util/renderModel';

const { userId, userContext, isWidget = false } = renderModel.get();
const isFullWidth = renderModel.isFullWidth();
const isInvokedFromHeader = renderModel.isInvokedFromHeader();
const searchParams = new URLSearchParams(window.location.search);

const referralQuery = searchParams.get('referral');
const collectionsData = searchParams.get('collectionsData');

const isMobileApp = MOBILE_APP_REFERRALS.includes(String(referralQuery));
const useClientSDK = isWidget && !isMobileApp;

const apiTimeout = collectionsData === 'true' ? 35000 : undefined;

async function getContext(parentUrl?: string): Promise<Context | {}> {
  if (userId) {
    try {
      fedopsLogger.interactionStarted('fetching-context');

      // TODO: fix `renderModel -> isWidget` state for chatbotOwnerApp(native Wix One App)
      // Now there is `isWidget === true` for Wix One App.
      // It's not correct because there is no widget script for Wix One App. So we can not use WidgetSDK.
      const context = useClientSDK
        ? await (this as WidgetSDK).getContext()
        : await contextProvider.getContext({ ...userContext, isWidget }, parentUrl);

      fedopsLogger.interactionEnded('fetching-context');

      return context;
    } catch (error) {
      logger.error({
        message: 'Failed to collect context',
        error,
      });
    }
  }
  return Promise.resolve({});
}

async function getWidgetPlacement(): Promise<BiDataWidgetPlacement | undefined> {
  if (useClientSDK) {
    return (this as WidgetSDK).getWidgetPlacement();
  }
  return Promise.resolve(getDefaultWidgetPlacement(isFullWidth, isInvokedFromHeader));
}

function getOrigParams(): Promise<OrigParams | {}> {
  if (useClientSDK) {
    return (this as WidgetSDK).getOrigParams();
  }
  return Promise.resolve({});
}

function getInitialParams(): Promise<InitialParams | {}> {
  if (useClientSDK) {
    return (this as WidgetSDK).getInitialParams();
  }
  return Promise.resolve({});
}

function getStartChatParams(): Promise<StartChatParams> {
  if (useClientSDK) {
    return (this as WidgetSDK).getStartChatParams();
  }
  return Promise.resolve(getStartChatParamsFromUrl());
}

function navigateTo(payload: NavigationParams): Promise<NavigationCompleted | void> | void {
  if (useClientSDK) {
    return (this as WidgetSDK).navigateTo(payload);
  }
}

const proxyMethods = {
  getContext,
  getWidgetPlacement,
  getInitialParams,
  getOrigParams,
  getStartChatParams,
  navigateTo,
};

export const widgetSDK = new Proxy(new WidgetSDK(apiTimeout), {
  get(target: WidgetSDK, prop: string | symbol): any {
    if (target.hasOwnProperty(prop)) {
      if (proxyMethods[prop]) {
        return (...args) => {
          return proxyMethods[prop].apply(target, args);
        };
      }
      return target[prop];
    }
  },
});

function getStartChatParamsFromUrl(): StartChatParams | undefined {
  const whitelistParams = [
    'nodeId',
    'ni',
    'versionId',
    'vi',
    'collectionsData',
    'aiPlatformVerificationSeed',
  ];

  if (whitelistParams.some((key) => searchParams.has(key))) {
    return Object.fromEntries(searchParams);
  }

  return undefined;
}
